import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

import { searchV2Service } from '@/services'
import {
  CargoMarkResult,
  PalletResult,
  PositionResult,
  SearchType,
  Summary
} from '@/services/SearchV2Service'

interface SearchState {
  loading: boolean
  querySearch: string
  queryType: SearchType | null
  summary: Summary
  searchResult: null | CargoMarkResult | PalletResult | PositionResult
}

interface SearchActions {
  openLoading: () => void
  closeLoading: () => void
  setQuerySearch: (querySearch: string) => void
  fetchQueryResult: () => Promise<void>
}

type SearchStore = SearchState & SearchActions

const initialState: SearchState = {
  loading: false,
  querySearch: '',
  queryType: null,
  summary: {
    total: 0,
    pending: 0,
    scanned: 0,
    pized: 0,
    stored: 0,
    waiting: 0
  },
  searchResult: null
}

export const useSearchStore = create<SearchStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        openLoading: () => set({ loading: true }),
        closeLoading: () => set({ loading: false }),
        setQuerySearch: (querySearch: string) => set({ querySearch }),

        fetchQueryResult: async () => {
          const { success, data } = await searchV2Service.search(
            get().querySearch
          )
          if (success && data) {
            set({
              queryType: data.queryType,
              summary: { ...data.summary },
              searchResult: data.searchResult
            })
          }
        }
      }),
      {
        name: 'search-store'
      }
    )
  )
)

export const useSearchState = () => {
  const loading = useSearchStore((state) => state.loading)
  const querySearch = useSearchStore((state) => state.querySearch)
  const queryType = useSearchStore((state) => state.queryType)
  const searchResult = useSearchStore((state) => state.searchResult)
  const summary = useSearchStore((state) => state.summary)
  return { loading, querySearch, searchResult, queryType, summary }
}

export const useSearchActions = () => {
  const openLoading = useSearchStore((state) => state.openLoading)
  const closeLoading = useSearchStore((state) => state.closeLoading)
  const setQuerySearch = useSearchStore((state) => state.setQuerySearch)
  const fetchQueryResult = useSearchStore((state) => state.fetchQueryResult)

  return { openLoading, closeLoading, setQuerySearch, fetchQueryResult }
}
